import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Upload, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';
import { docLibrary } from '@/api/client';
import { errorText } from '@/lib/permissionError';

const MAX_MB = 200;
const BLOCKED_MIME_PREFIXES = ['application/x-msdownload', 'application/x-sh', 'application/x-executable'];

/**
 * Document Library upload dialog — a 7th copy of this codebase's
 * established drag-drop-zone + progress-bar + uploadWithProgress pattern
 * (see e.g. `pages/binder/components/NewDocumentSlotDialog.tsx`),
 * duplicated per this project's own convention rather than extracted into
 * a shared module. Not PDF-only — this is a general shared drive. No
 * client-side checksum here (unlike the eISF flow) — the Document
 * Library has no checksum pipeline in v1, so there's no field to send it
 * to.
 *
 * 2026-09-07 fixes:
 *   - The dialog is a single reused instance across opens (no `key` forces
 *     a remount) — without resetting on open, a previous upload's
 *     `isSuccess` state leaked into the next open, showing "Uploaded!"
 *     immediately even though nothing had been uploaded yet this time.
 *     Same bug NewDocumentSlotDialog.tsx already fixed; applying the same
 *     reset-on-open pattern here.
 *   - Multi-file support: the backend has no batch endpoint, so multiple
 *     files upload sequentially through the same single-file presign →
 *     PUT → finalise flow, one after another, with a running "N of M"
 *     indicator instead of one shared progress bar.
 */
async function uploadWithProgress(
  url: string,
  file: File,
  mime: string,
  onProgress: (pct: number) => void,
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', mime);
    xhr.setRequestHeader('x-amz-server-side-encryption', 'AES256');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`S3 upload failed: ${xhr.status}`));
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });
}

interface QueueState {
  total: number;
  done: number;
  currentFileName: string | null;
  currentProgress: number | null;
  failed: Array<{ file_name: string; error: string }>;
}

export function UploadToLibraryDialog({
  open,
  onClose,
  folderId,
  folderLabel,
  onUploaded,
}: {
  open: boolean;
  onClose: () => void;
  /** Undefined = uploading to the root (no folder). */
  folderId: string | undefined;
  folderLabel: string;
  onUploaded: () => void;
}) {
  const qc = useQueryClient();
  const [dragOver, setDragOver] = useState(false);
  const [queueState, setQueueState] = useState<QueueState | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  // Reset all upload state whenever the dialog opens — this instance is
  // reused across opens, so without this a prior run's success/failure
  // state was still showing when the coordinator opened it again.
  useEffect(() => {
    if (open) {
      setQueueState(null);
      setSucceeded(false);
      setDragOver(false);
      uploadMut.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const uploadMut = useMutation({
    mutationFn: async (files: File[]) => {
      const failed: QueueState['failed'] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i]!;
        setQueueState({
          total: files.length,
          done: i,
          currentFileName: file.name,
          currentProgress: 0,
          failed,
        });
        try {
          const mime = file.type || 'application/octet-stream';
          const presigned = await docLibrary.presignUpload({
            folder_id: folderId,
            file_name: file.name,
            mime_type: mime,
          });
          await uploadWithProgress(presigned.upload_url, file, mime, (pct) =>
            setQueueState((prev) => (prev ? { ...prev, currentProgress: pct } : prev)),
          );
          await docLibrary.finaliseUpload({
            file_id: presigned.file_id,
            folder_id: folderId,
            file_name: file.name,
            file_size: file.size,
            mime_type: mime,
            s3_bucket: presigned.s3_bucket,
            s3_key: presigned.s3_key,
          });
        } catch (err) {
          failed.push({
            file_name: file.name,
            error: errorText(err, 'Upload failed'),
          });
        }
      }
      setQueueState({ total: files.length, done: files.length, currentFileName: null, currentProgress: null, failed });
      return { total: files.length, failedCount: failed.length };
    },
    onSuccess: ({ total, failedCount }) => {
      qc.invalidateQueries({ queryKey: ['doc-library-files'] });
      onUploaded();
      if (failedCount === 0) {
        toast.success(total === 1 ? 'Uploaded successfully' : `Uploaded ${total} files successfully`);
        setSucceeded(true);
        onClose();
      } else {
        toast.error(`${failedCount} of ${total} file(s) failed to upload — see details below.`);
        setSucceeded(false);
      }
    },
    onError: (err) => {
      toast.error(errorText(err, 'Upload failed'));
    },
  });

  function handleFiles(fileList: FileList | File[]) {
    const files = Array.from(fileList);
    if (files.length === 0) return;
    const valid: File[] = [];
    for (const file of files) {
      if (BLOCKED_MIME_PREFIXES.some((p) => (file.type || '').startsWith(p))) {
        toast.error(`"${file.name}": executable/script file types are not accepted.`);
        continue;
      }
      if (file.size === 0) {
        toast.error(`"${file.name}": cannot upload an empty file.`);
        continue;
      }
      if (file.size > MAX_MB * 1024 * 1024) {
        toast.error(`"${file.name}": must be under ${MAX_MB} MB.`);
        continue;
      }
      valid.push(file);
    }
    if (valid.length === 0) return;
    uploadMut.mutate(valid);
  }

  if (!open) return null;
  const isUploading = uploadMut.isPending;
  const hasFailures = (queueState?.failed.length ?? 0) > 0 && !isUploading;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center !m-0 bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isUploading) onClose();
      }}
    >
      <div className="card w-full max-w-lg space-y-4 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-ink-primary">Upload to Document Library</h2>
            <p className="mt-0.5 text-xs text-ink-muted">
              Filing to: <span className="font-medium text-ink-primary">{folderLabel}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isUploading}
            className="text-ink-muted hover:text-ink-primary disabled:opacity-40"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div
          className={`relative flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-10 text-center transition-colors ${
            dragOver
              ? 'border-accent-500 bg-accent-50'
              : 'cursor-pointer border-surface-border hover:border-accent-400 hover:bg-surface-muted/40'
          }`}
          onClick={() => !isUploading && document.getElementById('doc-library-file-input')?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            if (!isUploading) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (isUploading) return;
            handleFiles(e.dataTransfer.files);
          }}
        >
          {isUploading && queueState ? (
            <>
              <Loader2 className="h-8 w-8 animate-spin text-accent-500" />
              <p className="text-sm font-medium text-ink-primary">
                {queueState.total > 1
                  ? `Uploading ${queueState.done + 1} of ${queueState.total} — ${queueState.currentFileName}`
                  : `Uploading…${queueState.currentProgress !== null ? ` ${queueState.currentProgress}%` : ''}`}
              </p>
              {queueState.currentProgress !== null && (
                <div className="h-1.5 w-48 overflow-hidden rounded-full bg-surface-border">
                  <div
                    className="h-full rounded-full bg-accent-500 transition-all"
                    style={{ width: `${queueState.currentProgress}%` }}
                  />
                </div>
              )}
            </>
          ) : succeeded ? (
            <>
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
              <p className="text-sm font-medium text-emerald-700">Uploaded!</p>
            </>
          ) : hasFailures ? (
            <>
              <AlertTriangle className="h-8 w-8 text-danger-500" />
              <p className="text-sm font-medium text-danger-700">
                {queueState!.failed.length} of {queueState!.total} file(s) failed
              </p>
              <ul className="mt-1 max-h-24 space-y-0.5 overflow-y-auto text-left text-xs text-danger-600">
                {queueState!.failed.map((f) => (
                  <li key={f.file_name}>
                    <span className="font-medium">{f.file_name}</span>: {f.error}
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-ink-muted">
                Drag more files here or <span className="text-accent-700 underline">browse</span> to retry.
              </p>
            </>
          ) : (
            <>
              <Upload className="h-8 w-8 text-ink-muted" />
              <p className="text-sm font-medium text-ink-primary">
                Drag files here or <span className="text-accent-700 underline">browse</span>
              </p>
              <p className="text-xs text-ink-muted">Any file type, max {MAX_MB} MB each — multiple files allowed</p>
            </>
          )}
          <input
            id="doc-library-file-input"
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) handleFiles(e.target.files);
              e.target.value = '';
            }}
            disabled={isUploading}
          />
        </div>
      </div>
    </div>
  );
}
